
import React, { useState, useEffect } from 'react';
import { UserProfile, UserRole } from './types';
import { auth, db, loginWithEmail, loginWithGoogle, logoutUser, registerWithEmail } from './services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, onSnapshot, orderBy, limit, where } from 'firebase/firestore';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import StudentManagement from './components/StudentManagement';
import ClassManagement from './components/ClassManagement';
import NotificationManagement from './components/NotificationManagement';
import AttendanceManagement from './components/AttendanceManagement';
import MarksManagement from './components/MarksManagement';
import ResourceManager from './components/ResourceManager';
import SubjectManagement from './components/SubjectManagement';
import LeaveManagement from './components/LeaveManagement';
import ChatSystem from './components/ChatSystem';
import ProfileManagement from './components/ProfileManagement';

const LOGO_URL = "https://i.ibb.co/NMthQdw/download-Photoroom.png";

const App: React.FC = () => {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [isRegistering, setIsRegistering] = useState(false);
    const [formData, setFormData] = useState({ email: '', password: '', name: '' });
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState('dashboard');
    const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
    const [appLoadTime] = useState(Date.now());
    const [activeToast, setActiveToast] = useState<{ title: string, message: string } | null>(null);
    
    // Shared state for navigation drill-downs
    const [activeSubjectFilter, setActiveSubjectFilter] = useState<string>('');

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const userRef = doc(db, 'users', firebaseUser.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    setUser(userSnap.data() as UserProfile);
                } else {
                    setUser(null);
                    await logoutUser();
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (isDarkMode) document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    // Global Notification Listener
    useEffect(() => {
        if (!user) return;

        // Use a slightly older time to account for clock skew, 
        // but we'll use a ref to track what we've already seen
        const startTime = Date.now() - 5000; 

        const q = query(
            collection(db, 'notifications'),
            where('createdAt', '>', startTime),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            console.log("Notification snapshot received, docs:", snapshot.size);
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    const data = change.doc.data();
                    const docId = change.doc.id;
                    
                    console.log("New notification detected:", data.title, "Created at:", data.createdAt, "App load time:", appLoadTime);
                    
                    // Check if this notification was created after the app loaded
                    // and if it's relevant to the user
                    if (data.createdAt > appLoadTime && (data.role === 'all' || data.role === user.role)) {
                        console.log("Triggering desktop notification for:", data.title);
                        
                        // Always show in-app toast
                        setActiveToast({ title: data.title, message: data.message });
                        setTimeout(() => setActiveToast(null), 8000);

                        if ("Notification" in window && Notification.permission === "granted") {
                            try {
                                const options = {
                                    body: data.message,
                                    icon: LOGO_URL,
                                    tag: docId, // Prevent duplicate notifications for the same ID
                                    badge: LOGO_URL, // Small icon for the status bar
                                    vibrate: [200, 100, 200], // Vibrate pattern for mobile
                                    data: { url: '/' }
                                };

                                // Prefer Service Worker for background/mobile support
                                if ('serviceWorker' in navigator) {
                                    navigator.serviceWorker.ready.then(registration => {
                                        registration.showNotification(data.title || "New Announcement", options);
                                    });
                                } else {
                                    new Notification(data.title || "New Announcement", options);
                                }
                            } catch (err) {
                                console.error("Notification error:", err);
                            }
                        }
                    }
                }
            });
        }, (error) => {
            console.error("Notification listener error:", error);
        });

        return () => unsubscribe();
    }, [user, appLoadTime]);

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isRegistering) {
                if (formData.password.length < 6) throw new Error("Password should be at least 6 characters");
                await registerWithEmail(formData.email, formData.password, formData.name);
            } else {
                await loginWithEmail(formData.email, formData.password);
            }
        } catch (err: any) {
            alert(err.message);
        }
        setLoading(false);
    };

    if (loading) return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8f9fa] dark:bg-dark-bg">
            <img src={LOGO_URL} className="w-20 h-20 object-contain mb-6 animate-pulse" alt="Logo" />
            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    if (!user) return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa] dark:bg-dark-bg p-4">
            <div className="bg-white dark:bg-dark-card w-full max-w-md rounded-[2.5rem] p-10 shadow-2xl border border-gray-100 dark:border-gray-800 transition-all duration-300">
                <div className="text-center mb-10">
                    <img src={LOGO_URL} className="w-24 h-24 object-contain mx-auto mb-4" alt="BSD Logo" />
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white">BSD Digital Portal</h1>
                    <p className="text-gray-500 text-sm mt-1">{isRegistering ? 'Enroll for session 2026-2027' : 'Active Session 2026-2027'}</p>
                </div>
                
                <form onSubmit={handleAuth} className="space-y-4">
                    {isRegistering && (
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Name</label>
                            <input required className="w-full bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3.5 dark:text-white outline-none border border-transparent focus:border-purple-500" placeholder="e.g. John Smith" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                        </div>
                    )}
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
                        <input required className="w-full bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3.5 dark:text-white outline-none border border-transparent focus:border-purple-500" type="email" placeholder="email@example.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Password</label>
                        <input required className="w-full bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3.5 dark:text-white outline-none border border-transparent focus:border-purple-500" type="password" placeholder="••••••••" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                    </div>
                    
                    <button type="submit" className="w-full py-4 bg-purple-600 text-white font-bold rounded-xl shadow-lg hover:bg-purple-700 transition-all active:scale-[0.98] mt-2">
                        {isRegistering ? 'Create Account' : 'Sign In'}
                    </button>
                </form>
                
                <div className="text-center mt-6">
                    <button onClick={() => { setIsRegistering(!isRegistering); setFormData({ email: '', password: '', name: '' }); }} className="text-xs font-bold text-purple-600 dark:text-purple-400 hover:underline">
                        {isRegistering ? 'Already have an account? Login' : 'Need an account? Sign up as student'}
                    </button>
                </div>

                <div className="my-8 flex items-center gap-4 text-gray-400 text-[10px] font-bold uppercase tracking-widest">
                    <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800"></div>
                    OR
                    <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800"></div>
                </div>

                <button onClick={() => loginWithGoogle()} className="w-full py-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 font-bold rounded-xl flex items-center justify-center gap-3 dark:text-white shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                    Continue with Google
                </button>
                
                <p className="text-[9px] text-gray-400 text-center mt-8 uppercase tracking-widest leading-relaxed">
                    By continuing, you agree to the BSD Digital <br/> Portal Terms of Service & Privacy Policy.
                </p>
            </div>
        </div>
    );

    const navigateToTab = (tab: string, filter: string = '') => {
        setCurrentTab(tab);
        setActiveSubjectFilter(filter);
    };

    const renderTab = () => {
        switch(currentTab) {
            case 'dashboard': return <Dashboard role={user.role} />;
            case 'students': 
                return user.role === UserRole.ADMIN ? <StudentManagement role={UserRole.STUDENT} /> : <Dashboard role={user.role} />;
            case 'staff': 
                return user.role === UserRole.ADMIN ? <StudentManagement role={UserRole.STAFF} /> : <Dashboard role={user.role} />;
            case 'classes': return <ClassManagement user={user} onClassClick={(tab) => navigateToTab(tab || 'subjects')} />;
            case 'subjects': return <SubjectManagement user={user} onSubjectClick={(subId) => navigateToTab('resources', subId)} onBack={() => navigateToTab('classes')} />;
            case 'leaves': return <LeaveManagement user={user} />;
            case 'notifications': return <NotificationManagement />;
            case 'attendance': 
            case 'my-attendance': return <AttendanceManagement user={user} />;
            case 'scores':
            case 'my-scores': return <MarksManagement user={user} />;
            case 'resources': return <ResourceManager user={user} initialSubjectId={activeSubjectFilter} onBack={() => navigateToTab('subjects')} />;
            case 'chat': return <ChatSystem user={user} />;
            case 'profile': return <ProfileManagement user={user} />;
            default: return <Dashboard role={user.role} />;
        }
    };

    return (
        <div className="min-h-screen bg-[#f8f9fa] dark:bg-dark-bg flex flex-col lg:flex-row">
            <Sidebar role={user.role} isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} currentTab={currentTab} setCurrentTab={navigateToTab} />
            
            {/* In-App Notification Toast */}
            {activeToast && (
                <div className="fixed top-6 right-6 z-[100] w-full max-w-sm animate-slideIn">
                    <div className="bg-white dark:bg-dark-card border-l-4 border-blue-600 p-6 rounded-2xl shadow-2xl flex items-start gap-4 relative overflow-hidden group">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 shrink-0">
                            <i className="fas fa-bullhorn"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-black dark:text-white mb-1 truncate">{activeToast.title}</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{activeToast.message}</p>
                        </div>
                        <button onClick={() => setActiveToast(null)} className="text-gray-400 hover:text-red-500 transition-colors">
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="absolute bottom-0 left-0 h-1 bg-blue-600/20 w-full">
                            <div className="h-full bg-blue-600 animate-[progress_8s_linear]"></div>
                        </div>
                    </div>
                </div>
            )}

            <main className="flex-1 lg:ml-[260px] p-4 lg:p-8 overflow-x-hidden">
                <header className="flex items-center justify-between mb-6 bg-white dark:bg-dark-sidebar p-3 lg:p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 sticky top-4 z-30 transition-theme">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden w-9 h-9 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg"><i className="fas fa-bars dark:text-white"></i></button>
                        <div className="flex flex-col">
                            <h2 className="text-sm font-black dark:text-white capitalize">{currentTab.replace('-', ' ')}</h2>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">Academic Portal v1.0</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button onClick={() => setIsDarkMode(!isDarkMode)} className="w-9 h-9 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-500 hover:text-purple-500 transition-colors">
                            <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
                        </button>
                        <div className="flex items-center gap-2 pl-4 border-l border-gray-100 dark:border-gray-800">
                            <div className="hidden sm:text-right sm:block">
                                <p className="text-xs font-bold dark:text-white leading-none mb-1">{user.displayName}</p>
                                <p className="text-[9px] text-purple-500 font-black uppercase tracking-widest">{user.role}</p>
                            </div>
                            <img src={user.photoURL} className="w-9 h-9 rounded-xl object-cover ring-2 ring-purple-100 dark:ring-purple-900/30" alt="Profile" />
                            <button onClick={() => logoutUser()} className="ml-2 w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                                <i className="fas fa-sign-out-alt"></i>
                            </button>
                        </div>
                    </div>
                </header>
                <div className="w-full animate-fadeIn">{renderTab()}</div>
            </main>
        </div>
    );
};

export default App;
